import React, { useEffect } from 'react';
import Hotel from '@mui/icons-material/Hotel';
import Storefront from '@mui/icons-material/Storefront';
import Insights from '@mui/icons-material/Insights';
import ContactPage from '@mui/icons-material/ContactPage';
import Close from '@mui/icons-material/Close';
import { LodgeFirstLogo } from 'pages/components/LFHeader/LFHeader';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppSelector } from 'redux/hooks';
import { selectDiscreteAccess, selectPermission } from 'redux/global';
import {
  canAccessCashierSalesTabs,
  canAccessDashboard,
  canAccessDepartmentsPage,
  canAccessInventoryPage,
} from 'pages/Settings/redux';
import { openSideBar } from 'pages/Navigation/utils';
import {
  ErpModuleId,
  HOSPITALITY_PATHS,
  getErpModuleFromPath,
} from '../hospitalityModulePaths';
import * as S from './ErpSidebar.styled';

const ERP_SIDEBAR_TABS: { id: ErpModuleId; label: string; Icon: typeof Hotel }[] = [
  { id: 'hospitality', label: 'Hospitality', Icon: Hotel },
  { id: 'sales_distribution', label: 'Sales & Distribution', Icon: Storefront },
  { id: 'reporting_analytics', label: 'Reporting & Oversight', Icon: Insights },
  { id: 'crm', label: 'Customer Management', Icon: ContactPage },
];

const landingPathForModule = (
  id: ErpModuleId,
  permission: number | null,
  discreteAccess: ReturnType<typeof selectDiscreteAccess>,
): string => {
  if (id === 'hospitality') {
    return canAccessDashboard(permission)
      ? HOSPITALITY_PATHS.dashboard
      : HOSPITALITY_PATHS.operations;
  }
  if (id === 'sales_distribution') {
    if (canAccessDepartmentsPage(permission)) return HOSPITALITY_PATHS.departments;
    if (canAccessInventoryPage(permission, discreteAccess)) return HOSPITALITY_PATHS.inventory;
    if (canAccessCashierSalesTabs(permission)) return HOSPITALITY_PATHS.sales;
    return HOSPITALITY_PATHS.departments;
  }
  if (id === 'reporting_analytics') {
    return HOSPITALITY_PATHS.audits;
  }
  return HOSPITALITY_PATHS.crmTab('customers');
};

const ErpSidebar = function ErpSidebar({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) {
  const { pathname } = useLocation();
  const history = useHistory();
  const permission = useAppSelector(selectPermission);
  const discreteAccess = useAppSelector(selectDiscreteAccess);
  const selectedModule = getErpModuleFromPath(pathname) || 'hospitality';

  useEffect(() => {
    if (!isVisible) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isVisible, onClose]);

  return (
    <>
      <S.Backdrop
        type="button"
        $isVisible={isVisible}
        aria-label="Close ERP sidebar"
        tabIndex={isVisible ? 0 : -1}
        onClick={onClose}
      />
      <S.Container
        $isVisible={isVisible}
        aria-label="ERP sidebar"
        aria-hidden={!isVisible}
      >
        <S.BrandMark>
          <LodgeFirstLogo showText />
        </S.BrandMark>
        <S.Tabs>
          {ERP_SIDEBAR_TABS.map(({ id, label, Icon }) => (
            <S.Tab
              key={id}
              type="button"
              $isSelected={id === selectedModule}
              aria-label={label}
              onClick={() => {
                // Close ERP overlay, then keep/show the hospitality sidebar with module pages.
                if (id !== selectedModule) {
                  history.push(landingPathForModule(id, permission, discreteAccess));
                }
                onClose();
                openSideBar();
              }}
            >
              <Icon fontSize="small" />
              <S.TabLabel>{label}</S.TabLabel>
            </S.Tab>
          ))}
        </S.Tabs>
        <S.CloseRow>
          <S.CloseButton type="button" aria-label="Close ERP sidebar" onClick={onClose}>
            <Close fontSize="small" />
            Close
          </S.CloseButton>
        </S.CloseRow>
      </S.Container>
    </>
  );
};

export default React.memo(ErpSidebar);
